/**
 * What has happened to a row, beside the row.
 *
 * **Newest first, which is the endpoint's own order** -- the same arrangement
 * the comments beside it use, and for the same reason: a list that grows at the
 * top needs no scroll anchoring to keep the reader's place.
 *
 * **One rendering for twenty-seven field types.** A change reads the way the
 * grid reads a cell, because it goes through the same `toHumanReadable` --
 * against the field as it was when the change was made rather than as it is
 * now. See `history.ts`, which is where all of that lives and none of it is
 * React.
 *
 * Read-only. Reverting a change is offered by the database-wide activity
 * history, which can address a whole operation or a whole batch at once; a
 * control here would only ever reach one entry of one row.
 */
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import { mergedPages } from '@/shared/lib/paged'
import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import type { FieldContext } from '../fields/behavior/behavior'
import { HistoryChanges } from './history-changes'
import {
  historyChanges,
  historyPhrase,
  isWorthShowing,
  useRowHistory,
  type HistoryChange,
  type HistoryEntry,
} from './history'

export function HistoryPanel({
  tableId,
  rowId,
  fields,
  context,
}: {
  tableId: number
  rowId: number
  /** Every field of the table, hidden ones included: this is about the row. */
  fields: readonly Field[]
  context: FieldContext
}) {
  const { t } = useTranslation()
  const history = useRowHistory(tableId, rowId)

  const entries = mergedPages(history.data)
    .map((entry) => ({ entry, changes: historyChanges(entry, fields, context) }))
    .filter(({ entry, changes }) => isWorthShowing(entry, changes))

  return (
    <section aria-label={t('history.plural')} className="flex min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {history.isPending ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        ) : entries.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">{t('history.none')}</p>
        ) : (
          <ul className="space-y-3">
            {entries.map(({ entry, changes }) => (
              <HistoryRow
                key={entry.id}
                entry={entry}
                changes={changes}
                context={context}
              />
            ))}
          </ul>
        )}

        {history.hasNextPage && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            disabled={history.isFetchingNextPage}
            onClick={() => {
              void history.fetchNextPage()
            }}
          >
            {t('history.more')}
          </Button>
        )}
      </div>
    </section>
  )
}

function HistoryRow({
  entry,
  changes,
  context,
}: {
  entry: HistoryEntry
  changes: HistoryChange[]
  context: FieldContext
}) {
  const { t } = useTranslation()
  const userId = useSessionStore((state) => state.user?.id)

  // The workspace's list first, the entry's own name second. The entry carries
  // the name that was current when the change was made, which is what keeps it
  // readable after the account has gone -- but while the account is still here,
  // somebody who has been renamed should read as who they are now.
  const who =
    entry.user.id === userId
      ? t('history.you')
      : (context.resolveUserName?.(entry.user.id) ?? entry.user.name)

  const phrase = historyPhrase(entry.action_type)

  return (
    <li className="rounded-md border bg-muted/40 px-2.5 py-2">
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{who}</span>{' '}
        {phrase === null
          ? // Named rather than described. An action nobody here recognises is
            // still a record of something, and calling it an update would be a
            // guess about what it did.
            t('history.did', { action: entry.action_type })
          : t(`history.${phrase}`)}
        {/* An undone or redone change is a change in its own right, and its
            before and after read in the direction it actually went. */}
        {entry.action_command_type === 'UNDO' && <> · {t('history.undone')}</>}
        {entry.action_command_type === 'REDO' && <> · {t('history.redone')}</>}
        {/* Platform staff signed in as the user. Their realm subject, not a name:
            nothing here can resolve it. */}
        {entry.impersonator_sub && (
          <> · {t('history.impersonatedBy', { sub: entry.impersonator_sub })}</>
        )}
        {' · '}
        {whenText(entry.timestamp)}
      </p>

      <HistoryChanges changes={changes} />
    </li>
  )
}
